// ============================================================================
// KonzeptPanel — Projekt-Daten-Workflow V1
// ----------------------------------------------------------------------------
// Projekt → Projektdaten laden/speichern → Konzept erzeugen → Dokument
// ENTWURF. Keine Freigabe, kein Versand, kein PDF.
// ============================================================================
import { Fragment, useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import type { ApiResponse } from "@energyos/shared-types";
import { apiClient } from "@/lib/api-client.js";
import { Alert } from "@/components/ui/Alert.js";
import { EmptyState } from "@/components/ui/EmptyState.js";
import {
  detectBundeslandFromAddress,
  applyPrefillToForm,
  FIELD_LABELS,
  MISSING_FIELD_LABELS,
} from "./konzept-prefill-utils.js";
import type { PrefillResultData } from "./konzept-prefill-utils.js";
import { StorageGuardrailBadge } from "./StorageGuardrailBadge.js";
import type { StorageGuardrailStatus } from "./StorageGuardrailBadge.js";
import { StorageB5WorksheetPanel } from "./StorageB5WorksheetPanel.js";

// ─── Typen ────────────────────────────────────────────────────────────────────

interface DraftListItem {
  id: string;
  kind: string;
  status: string;
  profile: string;
  createdByUserId: string;
  createdAt: string;
  layoutVariant?: string | null;
  customerSegment?: string | null;
  emphasis?: string[] | null;
  styleRationale?: string | null;
  missingDataCount?: number | null;
  accentVariant?: string | null;
}

interface DocumentListItem {
  id: string;
  title: string;
  status: string;
  type: string;
  format: string;
  draftId: string | null;
  fileName: string;
  canApprove: boolean;
  requiresHumanReview: boolean;
  createdAt: string;
}

interface SavedInput {
  // Seit Migration 0010 persistiert: gespeichertes Bundesland hat beim Laden
  // Vorrang vor der Adress-Ableitung (manuelle Wahl bleibt reload-fest).
  bundesland?: string | null;
  jahresverbrauchKwh: number | null;
  strompreisCtProKwh: number | null;
  einspeisetarifCtProKwh: number | null;
  kwp: number | null;
  speicherKwh: number | null;
  speicherInvestEur: number | null;
  investitionAEur: number | null;
  investitionBEur: number | null;
  notstromFaehig: boolean | null;
  empfehlung: string | null;
  notes: string | null;
}

// ─── Konstanten ───────────────────────────────────────────────────────────────

const BUNDESLAENDER = [
  "Burgenland", "Kärnten", "Niederösterreich", "Oberösterreich",
  "Salzburg", "Steiermark", "Tirol", "Vorarlberg", "Wien",
];

const VARIANT_MAP: Record<string, { label: string; bg: string; fg: string }> = {
  classic_expert: { label: "Klassik", bg: "#f3f4f6", fg: "#374151" },
  homeowner_clear: { label: "Privat klar", bg: "#eff6ff", fg: "#1d4ed8" },
  investment_focus: { label: "Investment", bg: "#fefce8", fg: "#854d0e" },
  autarky_focus: { label: "Autarkie", bg: "#f0fdf4", fg: "#15803d" },
  technical_planning: { label: "Technisch", bg: "#f5f3ff", fg: "#6d28d9" },
};

const SEGMENT_LABELS: Record<string, string> = {
  private: "Privat",
  commercial: "Gewerbe",
  farm: "Landwirtschaft",
  housing_assoc: "WEG",
  public: "Öffentlich",
  unknown: "—",
};

const EMPHASIS_LABELS: Record<string, string> = {
  speicher: "Speicher",
  notstrom: "Notstrom",
  foerderung: "Förderung",
  schnelle_amortisation: "Amort.",
  hohe_autarkie: "Autarkie",
};

// ─── Auth-Token ────────────────────────────────────────────────────────────────

function authToken(): string | null {
  try {
    const stored = localStorage.getItem("energyos-auth");
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { state?: { user?: { token?: string } } };
    return parsed.state?.user?.token ?? null;
  } catch {
    return null;
  }
}

// ─── Kleine Hilfskomponenten ──────────────────────────────────────────────────

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block", width: 12, height: 12,
        border: "2px solid currentColor", borderTopColor: "transparent",
        borderRadius: "50%", animation: "spin 0.7s linear infinite",
        marginRight: 6, verticalAlign: "middle",
      }}
    />
  );
}

function StyleVariantChip({ variant, rationale }: { variant: string | null | undefined; rationale?: string | null }) {
  if (!variant) return null;
  const s = VARIANT_MAP[variant] ?? { label: variant, bg: "#f3f4f6", fg: "#374151" };
  return (
    <span title={rationale ?? variant}
      style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap", cursor: "default" }}>
      {s.label}
    </span>
  );
}

function StyleProfileBadge({ variant, segment, emphasis, missingDataCount, rationale }: {
  variant?: string | null; segment?: string | null; emphasis?: string[] | null;
  missingDataCount?: number | null; rationale?: string | null;
}) {
  const segLabel = segment ? (SEGMENT_LABELS[segment] ?? segment) : null;
  const emTags = (emphasis ?? []).filter((e) => EMPHASIS_LABELS[e]);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
      <StyleVariantChip variant={variant} rationale={rationale ?? null} />
      {segLabel && <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontWeight: 500 }}>{segLabel}</span>}
      {emTags.map((e) => (
        <span key={e} style={{ fontSize: 10, background: "#f1f5f9", color: "#475569", padding: "1px 6px", borderRadius: 999, whiteSpace: "nowrap" }}>
          {EMPHASIS_LABELS[e]}
        </span>
      ))}
      {missingDataCount != null && missingDataCount > 0 && (
        <span style={{ fontSize: 10, background: "#fff7ed", color: "#c2410c", padding: "1px 6px", borderRadius: 999, whiteSpace: "nowrap" }}>
          ⚠ {missingDataCount} Annahme{missingDataCount !== 1 ? "n" : ""}
        </span>
      )}
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    draft: { bg: "#eef2ff", fg: "#4338ca", label: "Entwurf" },
    needs_user_review: { bg: "#fff7ed", fg: "#c2410c", label: "Prüfung nötig" },
    qa_ready: { bg: "#ecfdf5", fg: "#047857", label: "QA-bereit" },
  };
  const s = map[status] ?? { bg: "#f3f4f6", fg: "#374151", label: status };
  return (
    <span style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

// ─── CostSummaryWidget ───────────────────────────────────────────────────────
// Lädt die neueste gespeicherte Kalkulation (GET /projects/:id/costing/calculations)
// und bietet einen Ein-Klick-Transfer des Netto-Gesamtpreises als Investition B.
// SEPARATER Datenpfad von der Wissensdatenbank (Kosten-Katalog ≠ Komponenten-KB).

interface CalcSummaryItem {
  id: string;
  name: string;
  status: string;
  updatedAt: string;
  resultJson: {
    data: {
      totalNetEur: number;
      totalGrossEur: number;
      pricePerKwpNetEur: number;
      quantities: { moduleCount: number };
    } | null;
    warnings: Array<{ code: string; message: string; severity: string }>;
  } | null;
}

function CostSummaryWidget({
  projectId,
  onTransferCost,
}: {
  projectId: string;
  onTransferCost: (totalNetEur: number) => void;
}) {
  const [latest, setLatest] = useState<CalcSummaryItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const res = await apiClient.get<{ items: CalcSummaryItem[] }>(`/projects/${projectId}/costing/calculations`);
      const items = res.success ? (res.data?.items ?? []) : [];
      if (items.length > 0) setLatest(items[0] ?? null);
      setLoading(false);
    })();
  }, [projectId]);

  if (loading) return null;

  if (!latest) {
    return (
      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 12px", marginBottom: 12, fontSize: 12, color: "var(--color-text-muted)" }}>
        💶 Noch keine gespeicherte Kalkulation für dieses Projekt — Tab „💶 Kalkulation" öffnen, Werte berechnen und speichern.
      </div>
    );
  }

  const data = latest.resultJson?.data;
  const warnings = latest.resultJson?.warnings ?? [];
  const fmtEur = (n: number) =>
    n.toLocaleString("de-AT", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
  const statusLabel: Record<string, string> = { draft: "Entwurf", needs_review: "Prüfung nötig", qa_ready: "QA-bereit" };

  return (
    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: "#0369a1" }}>💶 Letzte Kalkulation</span>
          <span style={{ fontSize: 11, color: "#64748b" }}>{latest.name}</span>
          <span style={{ fontSize: 11, background: "#e0f2fe", color: "#0369a1", padding: "1px 7px", borderRadius: 999, fontWeight: 600 }}>
            {statusLabel[latest.status] ?? latest.status}
          </span>
        </div>
        <span style={{ fontSize: 10, color: "#94a3b8", whiteSpace: "nowrap" }}>
          {new Date(latest.updatedAt).toLocaleDateString("de-AT")}
        </span>
      </div>

      {data ? (
        <>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Netto gesamt</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#0c4a6e" }}>{fmtEur(data.totalNetEur)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Brutto (inkl. MwSt.)</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#0c4a6e" }}>{fmtEur(data.totalGrossEur)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Module</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#0c4a6e" }}>{data.quantities.moduleCount}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#64748b" }}>€/kWp (netto)</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#0c4a6e" }}>{fmtEur(data.pricePerKwpNetEur)}</div>
            </div>
          </div>

          {warnings.length > 0 && (
            <div style={{ fontSize: 11, color: "#92400e", marginBottom: 8 }}>
              ⚠ {warnings.map((w) => w.message).join(" · ")}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn-secondary"
              style={{ fontSize: 12 }}
              onClick={() => onTransferCost(data.totalNetEur)}
              title="Übernimmt den Netto-Gesamtpreis als Investition Var. B (überschreibbar)"
            >
              ↓ Als Investition B übernehmen
            </button>
            <span style={{ fontSize: 10, color: "#94a3b8" }}>
              Interne Kalkulation · Entwurf · Menschliche Prüfung erforderlich · kein Angebot
            </span>
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: "#92400e" }}>
          Kalkulations-Ergebnis nicht verfügbar (Engine hat keinen Wert geliefert).
        </div>
      )}
    </div>
  );
}

// ─── PrefillBanner ────────────────────────────────────────────────────────────

function PrefillBanner({ result, filledFields }: { result: PrefillResultData; filledFields: string[] }) {
  return (
    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: "#0369a1", marginBottom: 8 }}>
        {result.hasSavedInput ? "💾 Gespeicherte Projektdaten geladen" : "📋 Projektdaten verfügbar"}
      </div>

      {/* Available fields */}
      <div style={{ marginBottom: 8 }}>
        {result.availableFields.map((f) => (
          <div key={f} style={{ color: "#0c4a6e", padding: "1px 0", fontSize: 11 }}>
            <span style={{ color: "#16a34a", marginRight: 4 }}>✓</span>
            {FIELD_LABELS[f] ?? f}
            {f === "customer.name" && result.source.customer?.name && (
              <span style={{ color: "#0369a1", marginLeft: 4 }}>— {result.source.customer.name}</span>
            )}
            {f === "project.address" && result.source.project?.address && (
              <span style={{ color: "#0369a1", marginLeft: 4 }}>— {result.source.project.address}</span>
            )}
          </div>
        ))}
      </div>

      {/* Auto-filled fields */}
      {filledFields.length > 0 && (
        <div style={{ marginBottom: 8, padding: "6px 8px", background: "#e0f2fe", borderRadius: 4 }}>
          <div style={{ fontWeight: 600, color: "#0369a1", marginBottom: 2, fontSize: 11 }}>Formular vorbefüllt:</div>
          {filledFields.map((f) => (
            <div key={f} style={{ color: "#0369a1", fontSize: 11 }}>· {f}</div>
          ))}
        </div>
      )}

      {/* Missing fields */}
      {result.missingFields.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 600, color: "#92400e", marginBottom: 3, fontSize: 11 }}>⚠ Noch manuell einzugeben:</div>
          {result.missingFields.map((f) => (
            <div key={f} style={{ color: "#92400e", padding: "1px 0", fontSize: 11 }}>
              · {MISSING_FIELD_LABELS[f] ?? f}
            </div>
          ))}
        </div>
      )}

      {/* Assumptions */}
      {result.assumptions.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 600, color: "#4b5563", marginBottom: 3, fontSize: 11 }}>Annahmen:</div>
          {result.assumptions.map((a, i) => (
            <div key={i} style={{ color: "#6b7280", padding: "1px 0", fontSize: 11 }}>· {a}</div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, color: "#92400e", fontWeight: 600, borderTop: "1px solid #fed7aa", paddingTop: 6, marginTop: 2 }}>
        ⚠ Bitte alle Werte vor der Erstellung prüfen — die Engine erfindet keine fehlenden Daten.
      </div>
    </div>
  );
}

// ─── InputStatusBar ───────────────────────────────────────────────────────────

function InputStatusBar({ hasSaved, saveSuccess, filledCount }: { hasSaved: boolean; saveSuccess: boolean; filledCount: number }) {
  const completeness = `${filledCount}/4 Pflichtfelder ausgefüllt`;
  if (saveSuccess) {
    return (
      <div style={{ fontSize: 11, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 4, padding: "4px 10px", marginBottom: 10 }}>
        ✓ Projektdaten gespeichert — werden beim nächsten Öffnen wiederhergestellt.{" "}
        <span style={{ opacity: 0.75 }}>· {completeness}</span>
      </div>
    );
  }
  if (hasSaved) {
    return (
      <div style={{ fontSize: 11, color: "#0369a1", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 4, padding: "4px 10px", marginBottom: 10 }}>
        💾 Gespeicherte Werte geladen. Änderungen mit „Projektdaten speichern" sichern.{" "}
        <span style={{ opacity: 0.75 }}>· {completeness}</span>
      </div>
    );
  }
  return (
    <div style={{ fontSize: 11, color: "#6b7280", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4, padding: "4px 10px", marginBottom: 10 }}>
      Noch keine gespeicherten Projektdaten. Werte eingeben und speichern.{" "}
      <span style={{ opacity: 0.75 }}>· {completeness}</span>
    </div>
  );
}

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────

export function KonzeptPanel({ projectId, projectAddress }: { projectId: string; projectAddress?: string }) {
  // Draft / Document state
  const [drafts, setDrafts] = useState<DraftListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [docBusy, setDocBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DocumentListItem[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsLoadError, setDocsLoadError] = useState<string | null>(null);
  const [docOpenBusy, setDocOpenBusy] = useState<string | null>(null);
  const [docDlBusy, setDocDlBusy] = useState<string | null>(null);
  const [docPrintBusy, setDocPrintBusy] = useState<string | null>(null);

  // Prefill state
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [prefillResult, setPrefillResult] = useState<PrefillResultData | null>(null);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [prefillFilledFields, setPrefillFilledFields] = useState<string[]>([]);

  // Project input save/load state
  const [hasSavedInput, setHasSavedInput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Tracks fields the user has manually changed — prefill/load never overwrites these.
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // ── Formularfelder ──────────────────────────────────────────────────────────
  // Bundesland: KEIN stiller Default (war fix "Kärnten" — E2E-Befund #4 bei
  // Tiroler Projekt). Reihenfolge: gespeicherter Wert > Ableitung aus
  // Projektadresse (PLZ/Stadt-Heuristik) > leer mit Pflichtfeld-Hinweis.
  const [bundesland, setBundesland] = useState("");
  const [bundeslandDetected, setBundeslandDetected] = useState<string | null>(null);
  const [kwp, setKwp] = useState("");
  const [verbrauch, setVerbrauch] = useState("");
  const [strompreis, setStrompreis] = useState("");
  const [einspeisetarif, setEinspeisetarif] = useState("");
  const [investitionA, setInvestitionA] = useState("");
  const [investitionB, setInvestitionB] = useState("");
  const [speicherKwh, setSpeicherKwh] = useState("");
  const [speicherInvestEur, setSpeicherInvestEur] = useState("");
  const [notstrom, setNotstrom] = useState(false);
  // Empfehlung A/B: war hartkodiert "B" — jetzt explizit wählbar.
  // Default sinnvoll: B (mit Speicher) wenn ein Speicher konfiguriert ist, sonst A.
  const [empfehlung, setEmpfehlung] = useState<"A" | "B">("B");
  // Hat der Nutzer die Empfehlung manuell gesetzt? Dann Auto-Default nicht mehr überschreiben.
  const [empfehlungTouched, setEmpfehlungTouched] = useState(false);

  const touchField = (field: string) => setTouchedFields((prev) => new Set([...prev, field]));
  const toNum = (s: string): number | null => { const n = parseFloat(s); return isNaN(n) ? null : n; };

  // ── Laden ───────────────────────────────────────────────────────────────────

  const loadDrafts = async () => {
    setLoading(true);
    setLoadError(null);
    const res = await apiClient.get<DraftListItem[]>(`/projects/${projectId}/konzept/drafts`);
    if (res.success) setDrafts(res.data ?? []);
    else setLoadError("Entwürfe konnten nicht geladen werden.");
    setLoading(false);
  };

  const loadDocuments = async () => {
    setDocsLoading(true);
    setDocsLoadError(null);
    const res = await apiClient.get<DocumentListItem[]>(`/projects/${projectId}/documents/konzept`);
    if (res.success) setDocuments(res.data ?? []);
    else setDocsLoadError("Dokumente konnten nicht geladen werden.");
    setDocsLoading(false);
  };

  // Lädt gespeicherte Projektdaten beim Mount — füllt Formular OHNE touchedFields zu setzen.
  const loadSavedInput = async (): Promise<boolean> => {
    const res = await apiClient.get<SavedInput | null>(`/projects/${projectId}/konzept/project-input`);
    const found = res.success && res.data != null;
    if (res.success && res.data) {
      const d = res.data;
      if (d.jahresverbrauchKwh != null) setVerbrauch(String(d.jahresverbrauchKwh));
      if (d.strompreisCtProKwh != null) setStrompreis(String(d.strompreisCtProKwh));
      if (d.einspeisetarifCtProKwh != null) setEinspeisetarif(String(d.einspeisetarifCtProKwh));
      if (d.kwp != null) setKwp(String(d.kwp));
      if (d.speicherKwh != null) setSpeicherKwh(String(d.speicherKwh));
      if (d.speicherInvestEur != null) setSpeicherInvestEur(String(d.speicherInvestEur));
      if (d.investitionAEur != null) setInvestitionA(String(d.investitionAEur));
      if (d.investitionBEur != null) setInvestitionB(String(d.investitionBEur));
      if (d.notstromFaehig != null) setNotstrom(d.notstromFaehig);
      setHasSavedInput(true);
    }
    // Bundesland: gespeicherter Wert hat Vorrang; sonst aus Projektadresse
    // ableiten (nur Vorbelegung — Nutzer kann immer übersteuern).
    const savedBundesland = res.success && res.data?.bundesland ? res.data.bundesland : null;
    if (savedBundesland) {
      setBundesland(savedBundesland);
    } else if (projectAddress) {
      const detected = detectBundeslandFromAddress(projectAddress);
      if (detected) {
        setBundesland(detected);
        setBundeslandDetected(detected);
      }
    }
    return found;
  };

  useEffect(() => {
    void loadDrafts();
    void loadDocuments();
    // Selbstläufer: gespeicherten Konzept-Input laden; fand sich keiner, die Prefill-Kette
    // (kWp/Costing/Verbrauch) automatisch ziehen — ohne Button-Klick. touchedFields-gegated,
    // überschreibt also keine manuelle Eingabe.
    void (async () => {
      const hadSaved = await loadSavedInput();
      if (!hadSaved) await runPrefill(true);
    })();
  }, [projectId]);

  // Variante A ist nur dann eine echte Alternative, wenn eine Investition A eingegeben wurde.
  const hasVarianteA = toNum(investitionA) != null;

  // Sinnvoller Auto-Default für die Empfehlung, solange der Nutzer nicht manuell wählt:
  //   Speicher konfiguriert → B (mit Speicher), sonst A — aber A nur, wenn Variante A existiert.
  useEffect(() => {
    if (empfehlungTouched) return;
    const hasStorage = (toNum(speicherKwh) ?? 0) > 0;
    if (hasStorage) setEmpfehlung("B");
    else setEmpfehlung(hasVarianteA ? "A" : "B");
  }, [speicherKwh, investitionA, empfehlungTouched, hasVarianteA]);

  // ── Aktionen ────────────────────────────────────────────────────────────────

  const runPrefill = async (silent: boolean) => {
    if (!silent) {
      setIsPrefilling(true);
      setPrefillError(null);
    }
    const res = await apiClient.get<PrefillResultData>(`/projects/${projectId}/konzept/prefill-source`);
    if (res.success && res.data) {
      const data = res.data;
      setPrefillResult(data);
      const filled: string[] = [];

      // Bundesland aus Adresse erkennen
      const address = data.source.project?.address ?? "";
      const detected = detectBundeslandFromAddress(address);
      if (detected && !touchedFields.has("bundesland")) {
        setBundesland(detected);
        filled.push(`Bundesland: ${detected} (aus Adresse erkannt)`);
      }

      // Alle numerischen Felder aus formData befüllen (touchedFields-Guard via applyPrefillToForm)
      const extraFilled = applyPrefillToForm(
        data.formData,
        {
          setKwp,
          setVerbrauch,
          setStrompreis,
          setEinspeisetarif,
          setInvestitionA,
          setInvestitionB: setInvestitionB,
          setSpeicherKwh,
          setNotstrom,
        },
        touchedFields,
      );
      filled.push(...extraFilled);
      setPrefillFilledFields(filled);
    } else if (!silent) {
      setPrefillError("Projektdaten konnten nicht geladen werden.");
    }
    if (!silent) setIsPrefilling(false);
  };

  const handlePrefill = () => runPrefill(false);

  const handleSaveInput = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    const body = {
      bundesland: bundesland || null,
      kwp: toNum(kwp),
      jahresverbrauchKwh: toNum(verbrauch),
      strompreisCtProKwh: toNum(strompreis),
      einspeisetarifCtProKwh: toNum(einspeisetarif),
      investitionAEur: toNum(investitionA),
      investitionBEur: toNum(investitionB),
      speicherKwh: toNum(speicherKwh),
      speicherInvestEur: toNum(speicherInvestEur),
      notstromFaehig: notstrom,
      empfehlung: null,
      notes: null,
    };
    const res = (await apiClient.put(`/projects/${projectId}/konzept/project-input`, body)) as ApiResponse<SavedInput>;
    if (res.success) {
      setHasSavedInput(true);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } else {
      setSaveError("Speichern fehlgeschlagen.");
    }
    setIsSaving(false);
  };

  const handleCreate = async () => {
    setIsGenerating(true);
    setGenError(null);
    const hasStorage = toNum(speicherKwh) != null && (toNum(speicherKwh) ?? 0) > 0;
    const body = {
      bundesland,
      anlage: {
        kwp: toNum(kwp),
        // null = Backend löst den echten PVGIS-Ertrag auf (statt hartkodierte 1000).
        spezErtragKwhPerKwp: null,
        ...(hasStorage ? { speicher: { kapazitaetKwh: toNum(speicherKwh), typ: null, nutzbarKwh: null, nachruestbarKwh: null } } : {}),
        ...(notstrom ? { notstrom: { faehig: true, umschaltzeitMs: null } } : {}),
      },
      consumption: {
        jahresverbrauchKwh: toNum(verbrauch),
        strompreisCtProKwh: toNum(strompreis),
        einspeisetarifCtProKwh: toNum(einspeisetarif),
        verbrauchArt: "rechnung" as const,
      },
      varianteA: toNum(investitionA) != null ? { investitionEur: toNum(investitionA) } : null,
      varianteB: { investitionEur: toNum(investitionB) },
      // Nutzerwahl statt Konstante; ohne echte Variante A immer B.
      empfehlung: hasVarianteA ? empfehlung : ("B" as const),
    };
    const res = (await apiClient.post(`/projects/${projectId}/konzept/drafts`, body)) as ApiResponse<DraftListItem>;
    if (res.success) {
      await loadDrafts();
    } else {
      setGenError("Konzept-Entwurf konnte nicht erzeugt werden.");
    }
    setIsGenerating(false);
  };

  const openPreview = async (draftId: string, fassung: "intern" | "kunde" = "intern") => {
    setOpening(fassung === "kunde" ? `${draftId}:kunde` : draftId);
    setActionError(null);
    try {
      const token = authToken();
      const qs = fassung === "kunde" ? "?fassung=kunde" : "";
      const res = await fetch(`/api/projects/${projectId}/konzept/drafts/${draftId}/preview-html${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { setActionError(`Vorschau nicht abrufbar (HTTP ${res.status}).`); return; }
      const html = await res.text();
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { setActionError("Vorschau konnte nicht geöffnet werden."); }
    finally { setOpening(null); }
  };

  const openDocument = async (docId: string) => {
    setDocOpenBusy(docId); setActionError(null); setSuccess(null);
    try {
      const token = authToken();
      const dl = await fetch(`/api/projects/${projectId}/documents/konzept/${docId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!dl.ok) { setActionError(`Dokument nicht abrufbar (HTTP ${dl.status}).`); return; }
      const html = await dl.text();
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { setActionError("Dokument konnte nicht geöffnet werden."); }
    finally { setDocOpenBusy(null); }
  };

  // #3a Ein-Klick-PDF: die druckfertige A4-HTML-Fassung holen, ein winziges
  // Auto-Print-Script injizieren und in neuem Tab öffnen → Browser-Druckdialog
  // startet direkt („Als PDF speichern"). Reine Anzeige, ENTWURF, keine Freigabe.
  const openPrintable = (html: string) => {
    const autoPrint =
      '<script>window.addEventListener("load",function(){setTimeout(function(){try{window.focus();window.print();}catch(e){}},400);});' +
      "</scr" + "ipt>";
    const withPrint = html.includes("</body>")
      ? html.replace("</body>", autoPrint + "</body>")
      : html + autoPrint;
    const url = URL.createObjectURL(new Blob([withPrint], { type: "text/html" }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const printDocument = async (docId: string) => {
    setDocPrintBusy(docId); setActionError(null); setSuccess(null);
    try {
      const token = authToken();
      const dl = await fetch(`/api/projects/${projectId}/documents/konzept/${docId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!dl.ok) { setActionError(`PDF-Ansicht nicht abrufbar (HTTP ${dl.status}).`); return; }
      openPrintable(await dl.text());
    } catch { setActionError("PDF-Druck konnte nicht gestartet werden."); }
    finally { setDocPrintBusy(null); }
  };

  const downloadDocument = async (docId: string, fileName: string) => {
    setDocDlBusy(docId); setActionError(null); setSuccess(null);
    try {
      const token = authToken();
      const dl = await fetch(`/api/projects/${projectId}/documents/konzept/${docId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!dl.ok) { setActionError(`Download nicht möglich (HTTP ${dl.status}).`); return; }
      const html = await dl.text();
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      const a = document.createElement("a");
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { setActionError("Download konnte nicht ausgeführt werden."); }
    finally { setDocDlBusy(null); }
  };

  const generateDocument = async (draftId: string) => {
    setDocBusy(draftId); setActionError(null); setSuccess(null);
    try {
      const res = (await apiClient.post(`/projects/${projectId}/documents/konzept`, { draftId })) as ApiResponse<{ id: string }>;
      if (!res.success || !res.data) { setActionError("Dokument konnte nicht erzeugt werden."); return; }
      const token = authToken();
      const dl = await fetch(`/api/projects/${projectId}/documents/konzept/${res.data.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!dl.ok) { setActionError(`Dokument nicht abrufbar (HTTP ${dl.status}).`); return; }
      const html = await dl.text();
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      void loadDocuments();
      setSuccess("Dokument erfolgreich erzeugt und in neuem Tab geöffnet.");
    } catch { setActionError("Dokument konnte nicht erzeugt werden."); }
    finally { setDocBusy(null); }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────
  const bundeslandAutoFilled = prefillFilledFields.some((f) => f.startsWith("Bundesland:"));

  const missingRequiredFields = useMemo(() => {
    const missing: string[] = [];
    if (!kwp.trim()) missing.push("Anlage (kWp)");
    if (!verbrauch.trim()) missing.push("Jahresverbrauch");
    if (!strompreis.trim()) missing.push("Strompreis");
    if (!bundesland) missing.push("Bundesland");
    return missing;
  }, [kwp, verbrauch, strompreis, bundesland]);

  const filledCount = 4 - missingRequiredFields.length;

  // ── Speicher-Guardrail-Status ─────────────────────────────────────────────────
  // Ableitung des StorageGuardrailStatus fuer die Badge-Anzeige.
  // Basiert auf prefillResult.storageEstimate + storageEval (aus prefill-source API).
  // Fehlende Anzeige: kein Prefill ausgefuehrt oder Speicher = 0.
  const storageGuardrailStatus = useMemo((): StorageGuardrailStatus => {
    const speicherNum = toNum(speicherKwh);
    if (speicherNum == null || speicherNum <= 0) return "no_data";

    const estimate = prefillResult?.storageEstimate;
    if (!estimate) return "model_draft"; // Speicher konfiguriert, aber noch kein Prefill

    if (estimate.missingInvestment || estimate.missingFields.length > 0) return "data_missing";

    const eval_ = prefillResult?.storageEval;
    if (!eval_ || eval_.data == null) return "data_missing";

    return "needs_review"; // Daten vollstaendig — immer menschliche Pruefung erforderlich
  }, [speicherKwh, prefillResult]);

  const storagePaybackDisplay = useMemo((): number | null => {
    const detail = prefillResult?.storageEval?.data?.detail;
    return detail?.storagePaybackYears ?? null;
  }, [prefillResult]);

  const storageMissingFields = useMemo((): string[] => {
    return prefillResult?.storageEstimate?.missingFields ?? [];
  }, [prefillResult]);

  const storageGuardrailText = useMemo((): string | null => {
    return prefillResult?.storageEval?.data?.detail?.guardrail ?? null;
  }, [prefillResult]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ENTWURF-Hinweis */}
      <Alert tone="warning" title="ENTWURF.">
        Konzepte sind unverbindliche Entwürfe — keine Freigabe, kein Versand, kein PDF.
        Die finale Freigabe ist ausschließlich ein menschlicher Schritt (die KI/Engine gibt nie frei).
      </Alert>

      {actionError && (
        <Alert tone="danger">
          {actionError}{" "}
          <button onClick={() => setActionError(null)}
            style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", fontSize: "inherit", padding: 0 }}>
            Schließen
          </button>
        </Alert>
      )}
      {success && <Alert tone="success">{success}</Alert>}

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* Formular: Projektdaten & Konzept-Entwurf                            */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Projektdaten &amp; Konzept erzeugen</h3>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "0 0 10px 0" }}>
          Kunde &amp; Adresse stammen autoritativ aus dem Projekt. Bundesland steuert die Förderung,
          Anlage/Verbrauch/Investition die Wirtschaftlichkeit. Fehlende Werte markiert die Engine
          als Annahme — sie erfindet nichts. Werte speichern, um sie beim nächsten Öffnen wiederzufinden.
        </p>

        <InputStatusBar hasSaved={hasSavedInput} saveSuccess={saveSuccess} filledCount={filledCount} />

        {/* Fehler-Alerts */}
        {prefillError && (
          <Alert tone="danger">
            {prefillError}{" "}
            <button onClick={() => setPrefillError(null)}
              style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", fontSize: "inherit", padding: 0 }}>
              Schließen
            </button>
          </Alert>
        )}
        {saveError && (
          <Alert tone="danger">
            {saveError}{" "}
            <button onClick={() => setSaveError(null)}
              style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", fontSize: "inherit", padding: 0 }}>
              Schließen
            </button>
          </Alert>
        )}

        {/* Schritt 1 — Abschnitt-Header */}
        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e3a5f", borderBottom: "1px solid #e9ecef", paddingBottom: 4, marginBottom: 12, marginTop: 4 }}>
          Schritt 1 — Projektdaten &amp; Verbrauch
        </div>

        {/* Prefill-Banner */}
        {prefillResult && <PrefillBanner result={prefillResult} filledFields={prefillFilledFields} />}

        {/* Zeile 1: Bundesland + Anlage + Verbrauch + Strompreis + Einspeisetarif */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 10 }}>
          <Field label={bundeslandAutoFilled ? "Bundesland (Förderung) ✓" : "Bundesland (Förderung)"}>
            <select value={bundesland}
              onChange={(e) => { setBundesland(e.target.value); touchField("bundesland"); }}
              style={{ ...fieldStyle, ...(bundeslandAutoFilled ? { borderColor: "#22c55e", background: "#f0fdf4" } : {}) }}>
              <option value="" disabled>— bitte wählen —</option>
              {BUNDESLAENDER.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            {bundeslandDetected && !touchedFields.has("bundesland") && (
              <div style={{ fontSize: 10, color: "#15803d", marginTop: 2 }}>
                aus Projektadresse abgeleitet — bitte prüfen
              </div>
            )}
          </Field>
          <Field label="Anlage (kWp)">
            <input type="number" min="0" step="0.1" value={kwp} placeholder="z.B. 10"
              onChange={(e) => { setKwp(e.target.value); touchField("kwp"); }} style={fieldStyle} />
          </Field>
          <Field label="Jahresverbrauch (kWh/a)">
            <input type="number" min="0" value={verbrauch} placeholder="z.B. 4500"
              onChange={(e) => { setVerbrauch(e.target.value); touchField("verbrauch"); }} style={fieldStyle} />
          </Field>
          <Field label="Strompreis (ct/kWh)">
            <input type="number" min="0" step="0.1" value={strompreis} placeholder="z.B. 28"
              onChange={(e) => { setStrompreis(e.target.value); touchField("strompreis"); }} style={fieldStyle} />
          </Field>
          <Field label="Einspeisetarif (ct/kWh)">
            <input type="number" min="0" step="0.1" value={einspeisetarif} placeholder="z.B. 8"
              onChange={(e) => { setEinspeisetarif(e.target.value); touchField("einspeisetarif"); }} style={fieldStyle} />
          </Field>
        </div>

        {/* Zeile 2: Investition A + B + Speicher + Speicher-Invest + Notstrom */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 12 }}>
          <Field label="Investition Var. A (€)">
            <input type="number" min="0" value={investitionA} placeholder="z.B. 14000"
              onChange={(e) => { setInvestitionA(e.target.value); touchField("investitionA"); }} style={fieldStyle} />
          </Field>
          <Field label="Investition Var. B (€)">
            <input type="number" min="0" value={investitionB} placeholder="z.B. 18000"
              onChange={(e) => { setInvestitionB(e.target.value); touchField("investitionB"); }} style={fieldStyle} />
          </Field>
          <Field label="Speicher (kWh)">
            <input type="number" min="0" step="0.1" value={speicherKwh} placeholder="z.B. 10"
              onChange={(e) => { setSpeicherKwh(e.target.value); touchField("speicherKwh"); }} style={fieldStyle} />
          </Field>
          <Field label="Speicher-Invest (€)">
            <input type="number" min="0" value={speicherInvestEur} placeholder="z.B. 7500"
              title="Separat vom PV-System — nur Speicher-Anteil"
              onChange={(e) => { setSpeicherInvestEur(e.target.value); touchField("speicherInvestEur"); }} style={fieldStyle} />
          </Field>
          <Field label="Notstrom-fähig">
            <div style={{ paddingTop: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={notstrom}
                  onChange={(e) => { setNotstrom(e.target.checked); touchField("notstrom"); }}
                  style={{ width: 14, height: 14 }} />
                Ja
              </label>
            </div>
          </Field>
        </div>

        {/* Speicher-Bewertung B5 — Tiefes Arbeitsblatt (Stage 2) */}
        <div style={{ marginBottom: 12 }}>
          <StorageB5WorksheetPanel
            initialValues={{
              speicherKwh: toNum(speicherKwh),
              speicherInvestEur: toNum(speicherInvestEur),
              strompreisCtProKwh: toNum(strompreis),
              einspeisetarifCtProKwh: toNum(einspeisetarif),
              pvSystemKwp: toNum(kwp),
            }}
            variant="inline"
          />
          {/* Kompakt-Status-Badge für schnellen Überblick (ergänzend) */}
          <div style={{ marginTop: 8 }}>
            <StorageGuardrailBadge
              status={storageGuardrailStatus}
              missingFields={storageMissingFields}
              paybackYearsEstimate={storagePaybackDisplay}
            />
          </div>
        </div>

        {/* Kostenübernahme aus Kalkulations-Tab (SEPARATER Datenpfad: Kosten-Katalog ≠ Komponenten-KB) */}
        <CostSummaryWidget
          projectId={projectId}
          onTransferCost={(totalNetEur) => {
            setInvestitionB(String(Math.round(totalNetEur)));
            touchField("investitionB");
          }}
        />

        {/* Schritt 2 — Abschnitt-Header */}
        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e3a5f", borderBottom: "1px solid #e9ecef", paddingBottom: 4, marginBottom: 12, marginTop: 4 }}>
          Schritt 2 — Konzept-Entwurf erzeugen
        </div>

        {/* Fehlende Pflichtfelder — Warnung */}
        {missingRequiredFields.length > 0 && (
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 13,
            marginBottom: 8,
          }}>
            <strong>Noch ausfüllen:</strong>{" "}
            {missingRequiredFields.join(" · ")}
          </div>
        )}

        {/* Empfehlung A/B — explizite Wahl statt hartkodiertem "B" */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 4 }}>
            Empfohlene Variante im Konzept
          </div>
          {hasVarianteA ? (
            <div style={{ display: "flex", gap: 16, fontSize: 13 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="empfehlung"
                  checked={empfehlung === "A"}
                  onChange={() => { setEmpfehlung("A"); setEmpfehlungTouched(true); }}
                />
                Variante A (ohne Speicher)
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="empfehlung"
                  checked={empfehlung === "B"}
                  onChange={() => { setEmpfehlung("B"); setEmpfehlungTouched(true); }}
                />
                Variante B (mit Speicher)
              </label>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
              Nur Variante B wird empfohlen — für eine Auswahl bitte zusätzlich eine
              Investition für Variante A eingeben.
            </div>
          )}
        </div>

        {/* Aktions-Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button className="btn-secondary" onClick={() => void handlePrefill()} disabled={isPrefilling}
            style={{ fontSize: 13 }} title="Liest Projektdaten + EnergyConsumer + Wirtschaftlichkeit + gespeicherten Input">
            {isPrefilling && <Spinner />}
            {isPrefilling ? "Lade…" : "Projektdaten übernehmen"}
          </button>
          <button className="btn-secondary" onClick={() => void handleSaveInput()} disabled={isSaving}
            style={{ fontSize: 13 }} title="Speichert die eingegebenen Werte persistent (GET /konzept/project-input beim nächsten Öffnen)">
            {isSaving && <Spinner />}
            {isSaving ? "Speichere…" : "Projektdaten speichern"}
          </button>
          <button className="btn-primary" onClick={() => void handleCreate()} disabled={isGenerating}
            style={{ fontSize: 13 }}>
            {isGenerating && <Spinner />}
            {isGenerating ? "Wird erstellt…" : "Konzept erzeugen"}
          </button>
        </div>
        <div style={{ fontSize: 12, color: missingRequiredFields.length === 0 ? "#198754" : "#6c757d", marginTop: 4 }}>
          {missingRequiredFields.length === 0
            ? "✓ Alle Pflichtfelder ausgefüllt"
            : `${missingRequiredFields.length} Pflichtfeld${missingRequiredFields.length > 1 ? "er" : ""} fehlt`}
        </div>

        {genError && (
          <Alert tone="danger" title="Fehler bei der Erstellung.">
            {genError}{" "}
            <button onClick={() => { setGenError(null); void handleCreate(); }}
              style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", fontSize: "inherit", padding: 0 }}>
              Erneut versuchen
            </button>
          </Alert>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* Entwürfe                                                             */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <div className="card">
        {/* Schritt 3 — Abschnitt-Header */}
        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e3a5f", borderBottom: "1px solid #e9ecef", paddingBottom: 4, marginBottom: 12 }}>
          Schritt 3 — Entwürfe &amp; Dokumente
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>Entwürfe</h3>
          <button className="btn-secondary" onClick={() => void loadDrafts()} disabled={loading} style={{ fontSize: 12 }}>
            {loading ? "Lade…" : "Aktualisieren"}
          </button>
        </div>
        {loadError ? (
          <Alert tone="danger">
            {loadError}{" "}
            <button onClick={() => void loadDrafts()}
              style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", fontSize: "inherit", padding: 0 }}>
              Erneut versuchen
            </button>
          </Alert>
        ) : loading ? (
          <div style={{ fontSize: 13, color: "var(--color-text-muted)", padding: "8px 0" }}>Lade Entwürfe…</div>
        ) : drafts.length === 0 ? (
          <EmptyState title="Noch kein Konzept-Entwurf" description="Erstellen Sie Ihr erstes Konzept oben." />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                {["Status", "Art", "Personalisierung", "Erstellt", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 12, color: "var(--color-text-muted)", fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drafts.map((d, idx) => (
                <Fragment key={d.id}>
                  <tr style={{ borderBottom: "none" }}>
                    <td style={{ padding: "8px 10px" }}><StatusChip status={d.status} /></td>
                    <td style={{ padding: "8px 10px", textTransform: "uppercase", color: "var(--color-text-muted)", fontSize: 12 }}>{d.kind} · {d.profile}</td>
                    <td style={{ padding: "8px 10px" }}>
                      <StyleProfileBadge variant={d.layoutVariant ?? null} segment={d.customerSegment ?? null}
                        emphasis={d.emphasis ?? null} missingDataCount={d.missingDataCount ?? null} rationale={d.styleRationale ?? null} />
                    </td>
                    <td style={{ padding: "8px 10px", color: "var(--color-text-muted)", fontSize: 12 }}>
                      {new Date(d.createdAt).toLocaleString("de-AT")}
                      {idx === 0 && hasSavedInput && (
                        <span style={{ fontSize: 11, color: "#6c757d", marginLeft: 6 }}>aus gespeicherten Projektdaten</span>
                      )}
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="btn-secondary" onClick={() => void openPreview(d.id)} disabled={opening === d.id} style={{ fontSize: 12 }} title="Interne Fassung (mit Preisen/Wirtschaftlichkeit)">
                        {opening === d.id ? "Öffne…" : "Vorschau"}
                      </button>
                      <button className="btn-secondary" onClick={() => void openPreview(d.id, "kunde")} disabled={opening === `${d.id}:kunde`} style={{ fontSize: 12, marginLeft: 8 }} title="Kundenfassung — Preise/interne Wirtschaftlichkeitsdetails ausgeblendet">
                        {opening === `${d.id}:kunde` ? "Öffne…" : "Kundenfassung"}
                      </button>
                      <button className="btn-primary" onClick={() => void generateDocument(d.id)} disabled={docBusy === d.id}
                        style={{ fontSize: 12, marginLeft: 8 }} title="Erzeugt ein persistiertes Konzept-Dokument (HTML, ENTWURF)">
                        {docBusy === d.id ? "Erzeuge…" : "Dokument"}
                      </button>
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--color-border)", background: "#fafafa" }}>
                    <td colSpan={5} style={{ padding: "2px 10px 6px 10px", fontSize: 11, color: "#6b7280" }}>
                      Kundenbezogen aufgebaut
                      {d.layoutVariant && <> · Layout: <strong>{d.layoutVariant}</strong></>}
                      {d.customerSegment && <> · Segment: <strong>{SEGMENT_LABELS[d.customerSegment] ?? d.customerSegment}</strong></>}
                      {(d.missingDataCount ?? 0) > 0
                        ? <> · <span style={{ color: "#c2410c" }}>{d.missingDataCount} Annahme(n) ergänzt</span></>
                        : <> · Vollständige Eingabedaten</>}
                      {" · "}<span style={{ color: "#dc2626", fontWeight: 600 }}>Nicht freigegeben (canApprove: false)</span>
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* Dokumente                                                            */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>Dokumente</h3>
          <button className="btn-secondary" onClick={() => void loadDocuments()} disabled={docsLoading} style={{ fontSize: 12 }}>
            {docsLoading ? "Lade…" : "Aktualisieren"}
          </button>
        </div>
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 11, color: "#15803d" }}>
          <strong>PDF-Export (V1):</strong> Druckansicht öffnen → im Browser Strg+P / ⌘+P → „Als PDF speichern".
          Das Dokument ist A4-optimiert (Seitenränder, Farben, Seitenumbrüche). ENTWURF-Wasserzeichen bleibt sichtbar.
        </div>
        <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: "0 0 12px 0" }}>
          Alle Dokumente sind unverbindliche <strong>Entwürfe</strong> (ENTWURF-Wasserzeichen, canApprove: false).
        </p>
        {docsLoadError ? (
          <Alert tone="danger">
            {docsLoadError}{" "}
            <button onClick={() => void loadDocuments()}
              style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", fontSize: "inherit", padding: 0 }}>
              Erneut versuchen
            </button>
          </Alert>
        ) : docsLoading ? (
          <div style={{ fontSize: 13, color: "var(--color-text-muted)", padding: "8px 0" }}>Lade Dokumente…</div>
        ) : documents.length === 0 ? (
          <EmptyState title="Noch kein Dokument" description="Speichern Sie einen Entwurf als Dokument." />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                {["Status", "Titel", "Draft-Bezug", "Erstellt", "Export"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 12, color: "var(--color-text-muted)", fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "8px 10px" }}><StatusChip status={doc.status} /></td>
                  <td style={{ padding: "8px 10px" }}>{doc.title}</td>
                  <td style={{ padding: "8px 10px", color: "var(--color-text-muted)", fontFamily: "monospace", fontSize: 11 }}>
                    {doc.draftId ? doc.draftId.slice(0, 8) + "…" : "—"}
                  </td>
                  <td style={{ padding: "8px 10px", color: "var(--color-text-muted)" }}>
                    {new Date(doc.createdAt).toLocaleString("de-AT")}
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="btn-primary" onClick={() => void printDocument(doc.id)} disabled={docPrintBusy === doc.id}
                      style={{ fontSize: 12 }} title="Öffnet die Druckansicht und startet direkt den PDF-Druckdialog (»Als PDF speichern«)">
                      {docPrintBusy === doc.id ? "Öffne…" : "Als PDF"}
                    </button>
                    <button className="btn-secondary" onClick={() => void openDocument(doc.id)} disabled={docOpenBusy === doc.id}
                      style={{ fontSize: 12, marginLeft: 8 }} title="Druckfertige Ansicht öffnen (dann Strg+P / ⌘+P)">
                      {docOpenBusy === doc.id ? "Öffne…" : "Druckansicht"}
                    </button>
                    <button className="btn-secondary" onClick={() => void downloadDocument(doc.id, doc.fileName)}
                      disabled={docDlBusy === doc.id} style={{ fontSize: 12, marginLeft: 8 }} title="HTML-Quelldatei herunterladen">
                      {docDlBusy === doc.id ? "Lade…" : "HTML speichern"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Stil-Helfer ──────────────────────────────────────────────────────────────

const fieldStyle: CSSProperties = {
  width: "100%", padding: "7px 9px",
  border: "1px solid var(--color-border)", borderRadius: 6,
  fontSize: 13, fontFamily: "inherit",
  background: "var(--color-bg)", color: "var(--color-text)",
  boxSizing: "border-box",
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontSize: 11, color: "var(--color-text-muted)", marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}
